import { FC, useState, useEffect } from 'react';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import { connect } from 'react-redux';
import { FormattedMessage } from 'dibs-react-intl';
import { Checkbox } from 'dibs-elements/exports/Checkbox';
import { setVatReverseCharge } from '../../actions/checkoutSubmitActions';
import { logCheckoutError } from '../../helpers/logCheckoutError';
import { transactionUpdateVatReverseChargeReview } from '../../mutations/TransactionUpdateVatReverseChargeReviewMutation';

import styles from './styles/CheckoutVatReverseChargeCheckbox.scss';

import { CheckoutVatReverseChargeCheckbox_viewer$key } from './__generated__/CheckoutVatReverseChargeCheckbox_viewer.graphql';
import { CheckoutVatReverseChargeCheckbox_transaction$key } from './__generated__/CheckoutVatReverseChargeCheckbox_transaction.graphql';

type Props = {
    viewer: CheckoutVatReverseChargeCheckbox_viewer$key;
    transaction: CheckoutVatReverseChargeCheckbox_transaction$key;
    isVatReverseCharge: boolean;
    isComputing: boolean;
    onSetVatReverseCharge: (isVatReverseCharge: boolean) => void;
};

const CheckoutVatReverseChargeCheckbox: FC<Props> = ({
    viewer: viewerRef,
    transaction: transactionRef,
    isVatReverseCharge,
    isComputing,
    onSetVatReverseCharge,
}) => {
    const viewer = useFragment(
        graphql`
            fragment CheckoutVatReverseChargeCheckbox_viewer on Viewer {
                isMultiStageCheckoutOn: featureFlag(feature: "multiStageCheckout")
            }
        `,
        viewerRef
    );
    const transaction = useFragment(
        graphql`
            fragment CheckoutVatReverseChargeCheckbox_transaction on Transaction {
                serviceId
                canBeVatReverseCharge
                reviews {
                    type
                    status
                }
            }
        `,
        transactionRef
    );
    const environment = useRelayEnvironment();
    const { reviews, serviceId: transactionId, canBeVatReverseCharge } = transaction || {};
    const { isMultiStageCheckoutOn } = viewer || {};
    const [isLoading, setIsLoading] = useState(false);

    const updateVatReverseChargeReview = async (isChecked: boolean): Promise<void> => {
        try {
            setIsLoading(true);
            await transactionUpdateVatReverseChargeReview(environment, {
                transactionId: transactionId || '',
                isChecked,
            });
        } catch (e) {
            logCheckoutError(e, 'vat');
        }
        onSetVatReverseCharge(isChecked);
        setIsLoading(false);
    };

    useEffect(() => {
        if (
            canBeVatReverseCharge &&
            reviews &&
            isMultiStageCheckoutOn &&
            !isComputing &&
            !isVatReverseCharge
        ) {
            const lastReview = [...reviews]
                .reverse()
                .find(e => e?.status === 'APPROVED' && e?.type === 'VAT_REVERSE_CHARGE');

            if (lastReview) {
                onSetVatReverseCharge(true);
            }
        }
    }, [
        canBeVatReverseCharge,
        reviews,
        isMultiStageCheckoutOn,
        isVatReverseCharge,
        isComputing,
        onSetVatReverseCharge,
    ]);

    if (!canBeVatReverseCharge && !isComputing) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                *
                <FormattedMessage
                    id="checkout.CheckoutReverseCargeCheckbox.reverseChargeHeader"
                    defaultMessage="1stDibs has a valid VAT identification number on file."
                />
            </div>
            <Checkbox
                name="isVatReverseCharge"
                checked={isVatReverseCharge}
                disabled={isLoading || isComputing}
                onChange={checked => {
                    if (viewer?.isMultiStageCheckoutOn) {
                        updateVatReverseChargeReview(checked);
                    } else {
                        onSetVatReverseCharge(checked);
                    }
                }}
                dataTn="vat-reverse-charge-checkbox"
                label={
                    <FormattedMessage
                        id="checkout.CheckoutReverseChargeCheckbox.reverseChargeLabel"
                        defaultMessage="Please check this box to apply reverse charge."
                    />
                }
                labelClass={styles.label}
                wrapperClass={styles.checkbox}
            />
        </div>
    );
};

type ReduxPayload = {
    transaction: {
        isVatReverseCharge: boolean;
    };
};

export default connect(
    ({ transaction }: ReduxPayload) => {
        return {
            isVatReverseCharge: transaction.isVatReverseCharge,
        };
    },
    {
        onSetVatReverseCharge: setVatReverseCharge,
    }
)(CheckoutVatReverseChargeCheckbox);
